/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from "@mui/system";
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { closeIcon, deleteIcon, saveIcon } from "assets/images";
import { Controller, useForm } from "react-hook-form";
import {
  COURSE_MATERIAL_TYPE,
  DOCUMENT_DESCRIPTION_MAX_LENGTH,
  DOCUMENT_NAME_MAX_LENGTH,
  INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
  MAX_SIZE_IN_BYTES,
  RESTRICTED_FILE_TYPES,
} from "utils/constants/constant";
import { IAddEditDocumentRequest } from "utils/interfaces/document";
import { useDropzone } from "react-dropzone";
import { onTextFieldKeyPress } from "utils/helper";
import { toast } from "react-toastify";
import {
  FILE_SIZE_EXCEEDED,
  ONLY_ONE_DOCUMENT_IS_ALLOWED_TO_UPLOAD,
  UNSUPPORTED_DOCUMENT,
  WHITE_SPACE_ERROR_SPACE,
} from "utils/constants/Messages";
import { ClearIcon } from "@mui/x-date-pickers";
import { ICourseMaterial } from "utils/interfaces/course";

interface IAddEditCourseMaterialProps {
  isEditDocument: boolean;
  isOpenDocumentModal: boolean;
  handleDocumentClose: () => void;
  handleSaveClick: (formData: IAddEditDocumentRequest) => void;
  editDocumentData?: ICourseMaterial;
}

const AddEditCourseMaterial: React.FC<IAddEditCourseMaterialProps> = ({
  isEditDocument,
  isOpenDocumentModal,
  handleDocumentClose,
  handleSaveClick,
  editDocumentData,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<IAddEditDocumentRequest>();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [showLink, setShowLink] = useState<boolean>(true);
  const [showDrop, setShowDrop] = useState<boolean>(true);
  const [showFileName, setShowFileName] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>();

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: (acceptedFiles: File[]) => {
      const isSingleDocument = acceptedFiles.length === 1;
      if (!isSingleDocument)
        toast.error(ONLY_ONE_DOCUMENT_IS_ALLOWED_TO_UPLOAD);
      const isFileNotValid = RESTRICTED_FILE_TYPES.includes(
        acceptedFiles[0].type
      );
      if (acceptedFiles[0]?.size > MAX_SIZE_IN_BYTES) {
        toast.error(FILE_SIZE_EXCEEDED);

        return;
      }
      if (isFileNotValid) toast.error(UNSUPPORTED_DOCUMENT);
      if (!isFileNotValid && isSingleDocument) {
        setUploadedFiles(acceptedFiles);
        setValue("document", acceptedFiles[0]);
        setShowFileName(true);
        setFileName(acceptedFiles[0].name);
      }
    },
    noClick: true,
    noKeyboard: true,
  });

  useEffect(() => {
    if (isEditDocument && editDocumentData) {
      setValue("name", editDocumentData?.name);
      setValue("description", editDocumentData?.description);
      setValue("type", editDocumentData?.type);
      setValue("link", editDocumentData?.link);
      if (!editDocumentData?.link) {
        setShowDrop(true);
        setShowLink(false);
        setShowFileName(true);
        setFileName(editDocumentData?.fileName);
      }
    } else {
      reset();
    }

    if (!isEditDocument) {
      setValue("name", "");
      setValue("description", "");
      setValue("type", "");
      setValue("link", "");
    }
  }, [editDocumentData, isEditDocument, setValue, reset]);

  useEffect(() => {
    if (isEditDocument && editDocumentData) {
      reset({
        name: editDocumentData?.name,
        description: editDocumentData?.description,
        type: editDocumentData?.type,
        link: editDocumentData?.link,
      });
      if (!editDocumentData?.link) {
        setShowDrop(true);
        setShowFileName(true);
        setShowLink(false);
        setFileName(editDocumentData?.fileName);
      }
    }
  }, [isOpenDocumentModal, editDocumentData, isEditDocument, reset]);

  const getNameError = (): string => {
    if (errors.name) {
      if (errors.name.type === "required") {
        return "Document Name is required";
      }

      if (errors.name.type === "maxLength") {
        return `Maximum length of Document name should be ${DOCUMENT_NAME_MAX_LENGTH}`;
      }

      if (errors.name.type === "pattern") {
        return `Document Name ${WHITE_SPACE_ERROR_SPACE}`;
      }
    }

    return "";
  };

  const getTypeError = (): string => {
    const selectedType = watch("type");
    if (!selectedType || selectedType === "") {
      return "Document Type is required";
    }

    return "";
  };

  const getFileOrLinkError = (): string => {
    const file = watch("document");
    const val = watch("link");
    if (
      errors?.link?.type === "required" ||
      errors?.document?.type === "required"
    ) {
      if (
        file === undefined &&
        (fileName === "" || fileName === undefined) &&
        (val === "" || val === null)
      ) {
        return "Document Link or File is required";
      }
    }
    if (errors?.link?.type === "pattern" && val !== "") {
      return `Document Link ${WHITE_SPACE_ERROR_SPACE}`;
    }

    return "";
  };

  const getDescriptionError = (): string => {
    if (errors.description) {
      if (errors.description.type === "maxLength") {
        return `Maximum length of description should be ${DOCUMENT_DESCRIPTION_MAX_LENGTH}`;
      }
      if (errors.description.type === "pattern") {
        return `Description should be ${WHITE_SPACE_ERROR_SPACE}`;
      }
    }

    return "";
  };

  const onModalClose = () => {
    setShowDrop(true);
    setShowLink(true);
    setUploadedFiles([]);
    setShowFileName(false);
    handleDocumentClose();
    setFileName("");
    reset();
  };

  useEffect(() => {
    const file = uploadedFiles[0];
    const val = watch("link");

    if (val !== "" && val !== null && val !== undefined && file === undefined) {
      setShowLink(true);
      setShowDrop(false);
    } else if (
      (file !== undefined || fileName !== "") &&
      (val === "" || val === null)
    ) {
      setShowLink(false);
      setShowDrop(true);
    } else {
      setShowDrop(true);
      setShowLink(true);
    }
    if (!isOpenDocumentModal) {
      setUploadedFiles([]);
      setFileName("");
      setShowFileName(false);
    }
  }, [watch("link"), watch("document"), editDocumentData, isOpenDocumentModal]);

  const handleDeleteDocument = () => {
    setShowFileName(false);
    setFileName("");
    setShowLink(true);
    setValue("document", undefined);
    setUploadedFiles([]);
  };

  return (
    <>
      <Modal open={isOpenDocumentModal} onClose={onModalClose}>
        <form onSubmit={handleSubmit(handleSaveClick)}>
          <Box className="common-modal repeat-modal">
            <Box className="modal-header">
              <Typography variant="h4">
                {isEditDocument
                  ? "Edit Course Material"
                  : "Add Course Material"}
              </Typography>
              <IconButton onClick={handleDocumentClose}>
                <img src={closeIcon} alt="close" />
              </IconButton>
            </Box>
            <Box className="modal-body">
              <Grid
                container
                columnSpacing={{ xs: "16px", md: "20px", lg: "24px" }}
              >
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="first-name"
                    label={
                      <>
                        Document Name <span className="color-red">*</span>
                      </>
                    }
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: Boolean(watch("name")),
                    }}
                    error={!!errors.name}
                    onKeyPress={(event) =>
                      onTextFieldKeyPress(event, watch("name")?.length)
                    }
                    helperText={getNameError()}
                    {...register("name", {
                      required: true,
                      maxLength: DOCUMENT_NAME_MAX_LENGTH,
                      pattern:
                        INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth className="select">
                    <InputLabel id="doctype-select-label">
                      <>
                        Document Type
                        <span className="color-red"> *</span>
                      </>
                    </InputLabel>
                    <Controller
                      name="type"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId="term-status-label"
                          id="type"
                          error={!!errors.type}
                          value={field.value}
                          displayEmpty
                          endAdornment={
                            field.value ? (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setValue("type", "");
                                }}
                                edge="end"
                                className="clear-btn"
                              >
                                <ClearIcon />
                              </IconButton>
                            ) : null
                          }
                        >
                          {COURSE_MATERIAL_TYPE?.map((status) => (
                            <MenuItem key={status.value} value={status.value}>
                              {status.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {!!errors.type && (
                      <FormHelperText error>{getTypeError()}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                {showLink && (
                  <Grid item xs={12}>
                    <TextField
                      id="link"
                      label="Document Link"
                      fullWidth
                      variant="outlined"
                      helperText={getFileOrLinkError()}
                      error={!!errors.link}
                      InputLabelProps={{
                        shrink: Boolean(watch("link")),
                      }}
                      {...register("link", {
                        required: showLink,
                        pattern:
                          INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
                      })}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    id="description"
                    label="Description"
                    className="textarea-add"
                    fullWidth
                    multiline
                    InputLabelProps={{
                      shrink: Boolean(watch("description")),
                    }}
                    variant="outlined"
                    error={!!errors.description}
                    helperText={getDescriptionError()}
                    {...register("description", {
                      maxLength: DOCUMENT_DESCRIPTION_MAX_LENGTH,
                      pattern:
                        INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
                    })}
                  />
                </Grid>
                {showDrop && (
                  <Grid item xs={12}>
                    <Box>
                      <Box className="dropbox-file-upload" {...getRootProps()}>
                        <input {...getInputProps()} />
                        <p className="document-modal-text">
                          Drag & Drop Your File Here
                        </p>
                        <p className="document-modal-or">OR</p>
                        <Button
                          className="upload-document-btn"
                          variant="outlined"
                          type="button"
                          onClick={open}
                        >
                          Browse Files
                        </Button>
                      </Box>
                      {!!errors.link && (
                        <FormHelperText error>
                          {getFileOrLinkError()}
                        </FormHelperText>
                      )}
                      {showFileName && (
                        <Box className="document-file-name">
                          <Typography variant="body1">{fileName}</Typography>
                          <IconButton
                            className="delete-file-btn"
                            onClick={() => handleDeleteDocument()}
                          >
                            <img src={deleteIcon} alt="delete" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
            <Box className="modal-footer" sx={{ marginTop: "0" }}>
              <Button variant="contained" className="btn-save" type="submit">
                <img src={saveIcon} alt="save" />
                Save
              </Button>
              <Button
                variant="outlined"
                className="btn-cancel"
                onClick={onModalClose}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default AddEditCourseMaterial;
